import React, { useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonIcon,
  IonSkeletonText,
  IonMenuButton,
  IonButtons,
  useIonRouter,
} from '@ionic/react';
import { Redirect } from 'react-router-dom';
import {
  chevronForward,
  people,
  musicalNotes,
  school,
  shield,
  layers,
  calendarOutline,
} from 'ionicons/icons';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useGroups } from '@/hooks/useGroups';
import { useAuthStore } from '@/store/authStore';
import { GroupCategory } from '@/types/group';
import { GROUP_CATEGORY_COLORS, GROUP_CATEGORY_LABELS } from '@/utils/labels';
import type { Group } from '@/types/group';

const WEEKDAY_LABELS: Record<number, string> = {
  0: 'duminică',
  1: 'luni',
  2: 'marți',
  3: 'miercuri',
  4: 'joi',
  5: 'vineri',
  6: 'sâmbătă',
};

const CADENCE_LABELS: Record<string, string> = {
  weekly: 'săptămânal',
  biweekly: 'bilunar',
  monthly: 'lunar',
  ad_hoc: 'ad-hoc',
};

const CATEGORY_ICONS: Record<string, string> = {
  [GroupCategory.SECTOR]: layers,
  [GroupCategory.TINERI]: people,
  [GroupCategory.MUZICAL]: musicalNotes,
  [GroupCategory.SBC]: school,
  [GroupCategory.ALTELE]: shield,
};

function greetingFor(hour: number): string {
  if (hour < 11) return 'Bună dimineața';
  if (hour < 18) return 'Bună ziua';
  return 'Bună seara';
}

function meetingLabel(group: Group): string {
  if (group.meeting_cadence === 'ad_hoc' || group.meeting_day === null) {
    return CADENCE_LABELS.ad_hoc ?? 'ad-hoc';
  }
  const day = WEEKDAY_LABELS[group.meeting_day] ?? '';
  const cadence = CADENCE_LABELS[group.meeting_cadence] ?? '';
  return cadence && day ? `${day} · ${cadence}` : day || cadence;
}

const MyGroupsPage: React.FC = () => {
  const router = useIonRouter();
  const user = useAuthStore((s) => s.user);
  const { data: groupsData, isLoading } = useGroups();

  const groups = useMemo<Group[]>(() => groupsData?.data ?? [], [groupsData]);

  const firstName = user?.first_name?.trim() || user?.name?.split(' ')[0] || 'Prieten';
  const now = new Date();
  const dateLabel = format(now, "EEEE, d MMMM", { locale: ro });
  const greeting = greetingFor(now.getHours());

  // Single group → jump straight in
  if (!isLoading && groups.length === 1 && groups[0]) {
    return <Redirect to={`/groups/${groups[0].id}`} />;
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Grupurile Mele</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '20px 20px 40px' }}>
          {/* Greeting */}
          <div className="mgx-hero crm-animate-in">
            <p className="mgx-hero__date">{dateLabel}</p>
            <h1 className="mgx-hero__title">
              {greeting}, <span className="mgx-hero__name">{firstName}</span>.
            </h1>
            <p className="mgx-hero__subtitle">
              {isLoading
                ? 'Se încarcă grupurile tale…'
                : groups.length === 0
                  ? 'Încă nu ești desemnat niciunui grup.'
                  : groups.length === 1
                    ? 'Un grup în îngrijire'
                    : `${groups.length} grupuri în îngrijire`}
            </p>
          </div>

          {/* Groups */}
          {isLoading ? (
            <div className="mgx-grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="crm-card" style={{ padding: 16 }}>
                  <IonSkeletonText animated style={{ width: '60%', height: 16 }} />
                  <IonSkeletonText animated style={{ width: '40%', height: 12, marginTop: 8 }} />
                  <IonSkeletonText animated style={{ width: '30%', height: 12, marginTop: 6 }} />
                </div>
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="crm-empty-state">
              <div className="crm-empty-state__icon"><IonIcon icon={people} /></div>
              <h3 className="crm-empty-state__title">Niciun grup atribuit</h3>
              <p className="crm-empty-state__text">
                Când un administrator te va atribui la un grup, îl vei vedea aici.
              </p>
            </div>
          ) : (
            <div className="mgx-grid">
              {groups.map((group) => {
                const catColor = GROUP_CATEGORY_COLORS[group.category] ?? '#64748b';
                const catIcon = CATEGORY_ICONS[group.category] ?? shield;
                const count = group.member_count ?? group.members_count ?? 0;
                return (
                  <button
                    key={group.id}
                    type="button"
                    className="mgx-card crm-animate-in"
                    onClick={() => router.push(`/groups/${group.id}`, 'forward')}
                    aria-label={`Deschide ${group.name}`}
                  >
                    <span
                      className="mgx-card__rail"
                      style={{ background: catColor }}
                      aria-hidden="true"
                    />
                    <span
                      className="mgx-card__icon"
                      style={{ backgroundColor: catColor }}
                    >
                      <IonIcon icon={catIcon} />
                    </span>
                    <span className="mgx-card__body">
                      <span className="mgx-card__name">{group.name}</span>
                      <span
                        className="crm-badge"
                        style={{
                          backgroundColor: `${catColor}15`,
                          color: catColor,
                          fontSize: 10,
                          padding: '1px 6px',
                          alignSelf: 'flex-start',
                        }}
                      >
                        {GROUP_CATEGORY_LABELS[group.category] ?? 'Grup'}
                      </span>
                      <span className="mgx-card__meta">
                        <span className="mgx-card__meta-item">
                          <IonIcon icon={people} style={{ fontSize: 12 }} />
                          {count} {count === 1 ? 'membru' : 'membri'}
                        </span>
                        <span className="mgx-card__meta-item">
                          <IonIcon icon={calendarOutline} style={{ fontSize: 12 }} />
                          {meetingLabel(group)}
                        </span>
                      </span>
                    </span>
                    <IonIcon icon={chevronForward} className="mgx-card__chevron" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default MyGroupsPage;
